import { Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as LayoutGrid02Icon } from "components/Icons/layout-grid-02.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import update from "immutability-helper";
import { isEqual, kebabCase } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Column as ColumnType } from "../OLTable";
import { Column } from "./Column";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    wrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingLeft: theme.spacing(8),
      borderTop: ({ fullView }: { fullView?: boolean }) =>
        fullView ? `1px solid ${theme.gray100}` : 0,
    },
    iconWrapper: {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
      padding: theme.spacing(5, 0),
    },
    icon: {
      color: theme.primary500,
      width: "18px",
      height: "18px",
      icon: "pointer",
    },
    title: {
      color: theme.primary500,
      textTransform: "uppercase",
      marginLeft: theme.spacing(4),
      fontWeight: 500,
      fontSize: "14px",
    },
    columnsWrapper: {
      display: "flex",
      flexDirection: "column",
    },
    listTitle: {
      backgroundColor: theme.gray50,
      fontSize: "14px",
      fontWeight: 500,
      padding: theme.spacing(6, 4),
      display: "flex",
      justifyContent: "space-between",
    },
    item: {
      padding: theme.spacing(4, 4),
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(8),
    },
    itemName: {
      display: "flex",
      alignItems: "center",
    },
    popover: {
      padding: 0,
      maxHeight: "373px",
    },
    li: {
      padding: 0,
    },
    hiddenCount: {
      marginLeft: theme.spacing(2),
    },
    label: {
      marginLeft: theme.spacing(6),
    },
    displayAll: {
      color: theme.primary500,
      textTransform: "uppercase",
      cursor: "pointer",
      marginLeft: theme.spacing(8),
    },
    fixedColumnsContainer: {
      display: "flex",
      alignItems: "center",
      width: "calc(100% - 16px)",
      height: "1px",
      backgroundColor: theme.gray100,
      justifyContent: "center",
      margin: theme.spacing(4, "auto"),
    },
    columnsContent: {
      padding: theme.spacing(2, 4),
    },
    staticContainer: {
      backgroundColor: theme.gray50,
      borderRadius: "6px",
    },
    fixedColumns: {
      display: "flex",
      alignItems: "center",
      zIndex: 1,
      background: theme.gray100,
      borderRadius: "40px",
      fontSize: 12,
      padding: theme.spacing(0, 4),
      position: "relative",
      "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: -8,
        width: "8px",
        height: "100%",
        backgroundColor: theme.white00,
      },
      "&:before": {
        content: '""',
        position: "absolute",
        bottom: 0,
        right: -8,
        width: "8px",
        height: "100%",
        backgroundColor: theme.white00,
      },
    },
  }),
);

interface Props {
  "data-test-id"?: string;
  children?: any;
  className?: string;
  colSpan?: number;
  columns?: ColumnType[];
  setColumns: (columns: ColumnType[]) => void;
  fullView?: boolean;
}

export const ColumnManager = ({
  "data-test-id": dataTestId,
  children,
  columns,
  setColumns: setOrderInColumns,
  fullView,
}: Props) => {
  const [orderedColumn, setColumns] = useState<ColumnType[]>([]);
  const contextMenu = useContextMenu();
  const classes = useStyles({ fullView: fullView });
  const { message } = useMessage();

  const hiddenColumnsCount = useMemo(
    () => (columns ? columns.filter((column) => !column.isVisible).length : 0),
    [columns],
  );
  const areHiddenColumns = hiddenColumnsCount > 0;

  useEffect(() => {
    const shouldUpdate = isEqual(columns, orderedColumn);
    if (columns && !shouldUpdate) {
      setColumns(columns);
    }
    // eslint-disable-next-line
  }, [columns]);

  const moveColumn = useCallback((dragIndex: number, hoverIndex: number) => {
    return setColumns((prevColumns) => {
      const draggedColumn = prevColumns[dragIndex];
      const hoverColumn = prevColumns[hoverIndex];
      const isDraggingSticky = draggedColumn.isSticky;
      const isHoverSticky = hoverColumn.isSticky;
      const stickyColumnsCount = prevColumns.filter(
        (col) => col.isSticky,
      ).length;
      const unStickyColumnsCount = prevColumns.filter(
        (col) => !col.isSticky,
      ).length;

      // Prevent move if only 1 column left
      if (isDraggingSticky && stickyColumnsCount === 1 && !isHoverSticky) {
        return prevColumns;
      }

      // Prevent move if only 1 column left
      if (!isDraggingSticky && unStickyColumnsCount === 1 && isHoverSticky) {
        return prevColumns;
      }

      // Update isSticky if crossing groups
      const updatedDraggedColumn = {
        ...draggedColumn,
        isSticky: isHoverSticky,
      };
      const updated = update(prevColumns, {
        $splice: [
          [dragIndex, 1], // remove dragged
          [hoverIndex, 0, updatedDraggedColumn], // insert updated
        ],
      });

      return updated;
    });
  }, []);

  const updateTable = useCallback(() => {
    setOrderInColumns(orderedColumn);

    // eslint-disable-next-line
  }, [orderedColumn]);

  const toggleVisibility = (isChecked: boolean, index: number) => {
    const updatedColumns = [...orderedColumn];
    updatedColumns[index].isVisible = isChecked;

    setOrderInColumns(updatedColumns);
  };

  const displayAllColumns = () => {
    const updatedColumns = [...orderedColumn].map((col) => ({
      ...col,
      isVisible: true,
    }));

    setOrderInColumns(updatedColumns);
  };

  const isOnlyOneColumnVisible = useMemo(
    () => (columns ? columns.length - hiddenColumnsCount === 1 : false),
    [columns, hiddenColumnsCount],
  );

  const renderColumns = useCallback(
    (column: ColumnType, index: number) => {
      return (
        <Column
          key={column.label}
          index={index}
          label={column.label}
          moveColumn={moveColumn}
          updateTable={updateTable}
          checked={!!column.isVisible}
          toggleVisibility={toggleVisibility}
          data-test-id={`table-column-manager-${kebabCase(column.label)}`}
          disableLastColumnSwitch={isOnlyOneColumnVisible}
        />
      );
    },
    // eslint-disable-next-line
    [orderedColumn, isOnlyOneColumnVisible],
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={classes.wrapper}>
        <div
          className={classes.iconWrapper}
          onClick={contextMenu.handleMenuOpen}
          data-test-id="table-column-manager-button"
        >
          <LayoutGrid02Icon className={classes.icon} />
          <Typography className={classes.title}>
            {message({ id: "manageColumns" })}
            {areHiddenColumns && (
              <span className={classes.hiddenCount}>
                ({hiddenColumnsCount})
              </span>
            )}
          </Typography>
        </div>
        <div data-test-id={dataTestId}>{children}</div>
        <Menu
          anchorEl={contextMenu.anchor}
          onClose={contextMenu.handleMenuClose}
          id="columns"
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          open={contextMenu.isMenuOpen}
          anchorReference="anchorEl"
          classes={{ list: classes.popover }}
          sx={{
            padding: 0,
          }}
        >
          <div className={classes.columnsWrapper}>
            <Typography className={classes.listTitle}>
              {message({ id: "listOfColumns" })}
              {areHiddenColumns && (
                <span
                  onClick={displayAllColumns}
                  className={classes.displayAll}
                  data-test-id="table-column-manager-display-all-button"
                >
                  {message({ id: "displayAll" })}
                </span>
              )}
            </Typography>
            <div className={classes.columnsContent}>
              <div className={classes.staticContainer}>
                {orderedColumn.map((column, i) => {
                  if (column.isSticky) {
                    return renderColumns(column, i);
                  }
                  return null;
                })}
              </div>
              <div className={classes.fixedColumnsContainer}>
                <div className={classes.fixedColumns}>Fixed columns</div>
              </div>
              <div>
                {orderedColumn.map((column, i) => {
                  if (!column.isSticky) {
                    return renderColumns(column, i);
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        </Menu>
      </div>
    </DndProvider>
  );
};
